import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import {
    AscLearningActivityCard,
    AscCalendarEvents,
    AscBadgesListCard,
    AscGoToChallengesCard,
    AscRanksCard,
    AscResourcesBanner,
    useResponsive,
    AscAnimationOnScroll,
} from '@ascendnow/ascend-components';
import { TRankPeriod } from '@ascendnow/shared';
import { PLATFORM_ROUTES } from '@platform/constants';
import styles from '@platform/styles/pages/home.module.scss';

import { useAppSelector, useAppDispatch } from '../../store';
import { getRankListQuery } from '../../store/rank/queries';
import { ReactComponent as EmployeesIllustration } from '../../assets/illustrations/recordingReport.svg';

export const AscDashboardAside = () => {
    const t = useTranslations('platform.dashboard');
    const { isMedium } = useResponsive();
    const dispatch = useAppDispatch();
    const router = useRouter();
    const { upcoming, fetchingUpcoming } = useAppSelector((state) => state.event);
    const {
        globalList: rankGlobalList,
        fetchingList: fetchingRankList,
        individualGlobalRank,
    } = useAppSelector((state) => state.rank);
    const { latestBadges, fetchingLatestBadges } = useAppSelector((state) => state.showcase.badge);
    const { fetchingSpentTime, spentTime } = useAppSelector((state) => state.dashboard.dashboard);

    return (
        <AscAnimationOnScroll className={styles.widgets} as="aside">
            {isMedium && <AscResourcesBanner color={'PURPLE'} variant={5} text={`“${t('banner')}.”`} />}
            <AscLearningActivityCard
                loading={fetchingSpentTime}
                className={styles.hours}
                learningHours={Math.round(spentTime?.hoursLoggedThisWeek)}
                completedPercentage={+spentTime?.percentageOfHoursLogged?.toFixed(1)}
                differenceToPreviousWeek={+spentTime?.percentageChange?.toFixed(1)}
            />
            <AscCalendarEvents
                loading={fetchingUpcoming}
                maxUpcomingEvents={2}
                events={
                    upcoming.length > 0
                        ? upcoming.map((e) => ({
                              id: e.id,
                              title: e.name,
                              date: e.date,
                              coverImageUrl: e?.thumbnail?.location,
                              type: {
                                  name: e?.eventType?.name,
                                  color: e?.eventType?.color,
                              },
                              eventPath: `${PLATFORM_ROUTES.events}/${e?.id}`,
                          }))
                        : []
                }
                onSeeMore={() => router.push(PLATFORM_ROUTES.events)}
            />
            <AscGoToChallengesCard
                className={styles.goToChallenges}
                urlPath={PLATFORM_ROUTES.challenges}
                loading={fetchingUpcoming}
                coverIllustration={<EmployeesIllustration className={styles.illustration} />}
                onButtonClick={() => router.push(PLATFORM_ROUTES.challenges)}
            />
            <AscBadgesListCard
                loading={fetchingLatestBadges}
                className={styles.badgesCard}
                title="Badges"
                badges={latestBadges.slice(0, 6)}
                seeAllLinkUrl={PLATFORM_ROUTES.showcase_badges_and_certificates_badges}
            />
            <AscRanksCard
                className={styles.pastWinnerCard}
                showPeriod
                loading={fetchingRankList}
                defaultPeriod="allTime"
                title="Global Rank"
                students={
                    individualGlobalRank && individualGlobalRank?.rank > 5
                        ? [...rankGlobalList.slice(0, 4), individualGlobalRank]
                        : rankGlobalList
                }
                onPeriodChange={(period: TRankPeriod) => {
                    dispatch(
                        getRankListQuery({
                            params: {
                                type: 'global',
                                period,
                                take: '5',
                                skip: '0',
                            },
                        })
                    );
                }}
                seeMorePath={PLATFORM_ROUTES.challenges_ranks_global}
            />
        </AscAnimationOnScroll>
    );
};
